// Render de citas inline [N1], [11], [fresh-3], [RAG-2] como chips
// clickeables + popup con detalle de la fuente.
//
// Diseñado para usarse con ReactMarkdown via la prop `components`:
//   <ReactMarkdown components={wrapWithCitations(onCite)}>...</ReactMarkdown>

import React, { useEffect } from 'react';
import styled from 'styled-components';
import { FaExternalLinkAlt } from 'react-icons/fa';

const accent = 'var(--btn-primary)';
const borderSoft = 'rgba(255, 255, 255, 0.08)';
const mono = "'JetBrains Mono', 'Consolas', monospace";

export const Cite = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 18px;
  padding: 0 5px;
  margin: 0 2px;
  font-family: ${mono};
  font-size: 0.66rem;
  font-weight: 700;
  line-height: 1;
  color: ${({ $missing }) => ($missing ? 'var(--alert-error)' : accent)};
  background: ${({ $missing }) =>
    $missing ? 'rgba(239, 71, 111, 0.1)' : 'rgba(0, 180, 216, 0.13)'};
  border: 1px solid
    ${({ $missing }) =>
      $missing ? 'rgba(239, 71, 111, 0.4)' : 'rgba(0, 180, 216, 0.38)'};
  border-radius: 4px;
  cursor: ${({ $missing }) => ($missing ? 'help' : 'pointer')};
  vertical-align: baseline;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ $missing }) =>
      $missing ? 'rgba(239, 71, 111, 0.18)' : 'rgba(0, 180, 216, 0.25)'};
    transform: translateY(-1px);
  }
`;

const PopupBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1500;
  background: rgba(8, 14, 24, 0.7);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  animation: arcanaFadeIn 0.15s ease;

  @keyframes arcanaFadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
`;

const PopupCard = styled.div`
  width: min(560px, 100%);
  max-height: calc(100vh - 2rem);
  display: flex;
  flex-direction: column;
  background: var(--color-primary);
  border: 1px solid rgba(0, 180, 216, 0.35);
  border-radius: 10px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(0, 180, 216, 0.08);
  padding: 1.1rem 1.25rem 1.25rem;
  position: relative;
  color: var(--text-secondary);

  .close {
    position: absolute;
    top: 10px;
    right: 12px;
    background: transparent;
    border: 1px solid ${borderSoft};
    color: var(--text-disabled);
    width: 26px;
    height: 26px;
    border-radius: 5px;
    cursor: pointer;
    font-size: 0.78rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.15s ease, border-color 0.15s ease;
    &:hover { color: var(--alert-error); border-color: rgba(239, 71, 111, 0.4); }
  }

  .idx {
    display: inline-block;
    font-family: ${mono};
    color: ${accent};
    font-size: 0.7rem;
    letter-spacing: 0.12em;
    background: rgba(0, 180, 216, 0.1);
    border: 1px solid rgba(0, 180, 216, 0.35);
    padding: 2px 8px;
    border-radius: 4px;
    margin-bottom: 0.6rem;
  }

  h3 {
    color: var(--text-primary);
    font-size: 1.02rem;
    margin: 0.1rem 0 0.55rem;
    line-height: 1.35;
  }

  .meta {
    font-family: ${mono};
    font-size: 0.66rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--text-disabled);
    margin-bottom: 0.7rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.55rem;
    align-items: center;

    .chip {
      background: rgba(11, 19, 32, 0.6);
      border: 1px solid ${borderSoft};
      padding: 3px 8px;
      border-radius: 4px;
    }
    .chip-low {
      color: var(--alert-warning, #f4a261);
      border-color: rgba(244, 162, 97, 0.4);
      background: rgba(244, 162, 97, 0.08);
    }
  }

  .extracto {
    color: var(--text-secondary);
    font-size: 0.86rem;
    line-height: 1.55;
    background: rgba(11, 19, 32, 0.5);
    border: 1px solid ${borderSoft};
    border-left: 2px solid ${accent};
    border-radius: 6px;
    padding: 0.6rem 0.8rem;
    margin: 0 0 0.85rem;
    white-space: pre-wrap;
    flex: 1 1 auto;
    min-height: 0;
    max-height: 50vh;
    overflow-y: auto;
    word-break: break-word;
  }

  .open {
    flex-shrink: 0;
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    color: ${accent};
    text-decoration: none;
    font-size: 0.86rem;
    padding: 0.45rem 0.75rem;
    border: 1px solid rgba(0, 180, 216, 0.35);
    border-radius: 6px;
    background: rgba(0, 180, 216, 0.06);
    transition: all 0.15s ease;
    &:hover {
      background: rgba(0, 180, 216, 0.18);
      transform: translateY(-1px);
    }
  }

  .missing {
    color: var(--alert-error);
    font-size: 0.85rem;
    padding: 0.55rem 0.7rem;
    background: rgba(239, 71, 111, 0.08);
    border: 1px solid rgba(239, 71, 111, 0.3);
    border-radius: 6px;
  }
`;

// Regex que captura: [N1], [11], [fresh-3], [RAG-2].
// Cluster compatible: [11][13][14].
export const CITE_RE = /\[(N|fresh-|RAG-|rag-)?(\d+)\]/g;

export function lookupSource(indexed, fresh, rag, prefix, num) {
  const norm = String(prefix || '').toLowerCase();
  const id = num;
  if (norm === 'fresh-') {
    return (fresh || []).find((s) => {
      const sid = String(s.idx || '').replace(/^fresh-/, '');
      return String(sid) === String(id);
    });
  }
  if (norm === 'rag-') {
    return (rag || []).find((s) => {
      const sid = String(s.idx || '').replace(/^RAG-/i, '');
      return String(sid) === String(id);
    });
  }
  return (indexed || []).find((s) => {
    const sid = String(s.idx || '').replace(/^N/, '');
    return String(sid) === String(id);
  });
}

export function renderWithCitations(node, onCite) {
  if (typeof node === 'string') {
    const parts = [];
    let last = 0;
    let m;
    CITE_RE.lastIndex = 0;
    while ((m = CITE_RE.exec(node)) !== null) {
      if (m.index > last) parts.push(node.slice(last, m.index));
      const prefix = m[1] || '';
      const num = m[2];
      parts.push({ __cite: true, prefix, num, raw: m[0] });
      last = m.index + m[0].length;
    }
    if (last < node.length) parts.push(node.slice(last));
    if (parts.length === 1 && typeof parts[0] === 'string') return node;
    return parts.map((p, i) => {
      if (typeof p === 'string') return p;
      const norm = String(p.prefix || '').toLowerCase();
      const label =
        norm === 'fresh-' ? `↗${p.num}` :
        norm === 'rag-' ? `◇${p.num}` :
        p.num;
      return (
        <Cite
          key={i}
          type="button"
          onClick={() => onCite({ prefix: p.prefix, num: p.num })}
          title={`Ver fuente ${p.raw}`}
        >
          {label}
        </Cite>
      );
    });
  }
  if (Array.isArray(node)) {
    return node.map((n, i) => (
      <React.Fragment key={i}>{renderWithCitations(n, onCite)}</React.Fragment>
    ));
  }
  if (React.isValidElement(node)) {
    const children = node.props.children;
    if (children === undefined || children === null) return node;
    return React.cloneElement(node, { ...node.props }, renderWithCitations(children, onCite));
  }
  return node;
}

export function wrapWithCitations(onCite) {
  const wrap = (Tag) => ({ children, ...rest }) =>
    <Tag {...rest}>{renderWithCitations(children, onCite)}</Tag>;
  return {
    p: wrap('p'),
    li: wrap('li'),
    td: wrap('td'),
    th: wrap('th'),
    strong: wrap('strong'),
    em: wrap('em'),
    h1: wrap('h1'),
    h2: wrap('h2'),
    h3: wrap('h3'),
    h4: wrap('h4'),
    blockquote: wrap('blockquote'),
  };
}

function formatDateShort(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('es-AR', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch { return iso; }
}

export function CitePopup({ cite, indexed, fresh, rag, onClose }) {
  // Hooks must run on every render even when cite is null — react-hooks rule.
  useEffect(() => {
    if (!cite) return undefined;
    const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [cite, onClose]);

  if (!cite) return null;
  const source = lookupSource(indexed, fresh, rag, cite.prefix, cite.num);
  const norm = String(cite.prefix || '').toLowerCase();
  const label =
    norm === 'fresh-' ? `fresh-${cite.num}` :
    norm === 'rag-' ? `RAG-${cite.num}` :
    `N${cite.num}`;
  const isRag = norm === 'rag-';

  return (
    <PopupBackdrop onClick={onClose}>
      <PopupCard onClick={(e) => e.stopPropagation()}>
        <button type="button" className="close" onClick={onClose} aria-label="Cerrar">×</button>
        <span className="idx">[{label}]</span>
        {source ? (
          <>
            <h3>{isRag ? (source.source_label || 'Chunk indexado') : (source.titulo || 'Sin título')}</h3>
            <div className="meta">
              {isRag && source.source_type && <span className="chip">tipo: {source.source_type}</span>}
              {isRag && typeof source.score === 'number' && (
                <span className="chip">score: {source.score.toFixed(3)}</span>
              )}
              {!isRag && source.fuente && <span className="chip">{source.fuente}</span>}
              {!isRag && source.fecha && <span className="chip">{formatDateShort(source.fecha)}</span>}
              {source.confianza && (
                <span className={`chip ${source.confianza === 'low' ? 'chip-low' : ''}`}>
                  Confianza: {source.confianza}
                </span>
              )}
              {norm === 'fresh-' && <span className="chip">Búsqueda en vivo</span>}
              {isRag && <span className="chip">Índice RAG (FTS + vector)</span>}
            </div>
            {source.extracto && <div className="extracto">{source.extracto}</div>}
            {source.url && (
              <a className="open" href={source.url} target="_blank" rel="noreferrer">
                Abrir fuente original <FaExternalLinkAlt style={{ fontSize: '0.72rem' }} />
              </a>
            )}
          </>
        ) : (
          <div className="missing">
            La cita <code>[{label}]</code> no pudo resolverse — el modelo citó un índice que no está en las fuentes devueltas. Probable error del LLM.
          </div>
        )}
      </PopupCard>
    </PopupBackdrop>
  );
}
